import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Theme } from "@omni/sdk/protocol";

import { load, save } from "./storage";

export type ThemePref = "system" | "light" | "dark";

// Generated from sdk/src/tokens.css (the style guide's source of truth).
const LIGHT: Record<string, string> = {
  "bg": "#F4F2EC",
  "surface": "#FFFFFF",
  "surface-2": "#EEEBE4",
  "border": "#DEDAD0",
  "border-strong": "#CFCAC0",
  "divider": "#EEEBE4",
  "text": "#16181D",
  "text-2": "#3C3F46",
  "text-muted": "#585C66",
  "accent": "#1E6B57",
  "accent-hover": "#185846",
  "accent-soft": "#E3EEE9",
  "accent-contrast": "#FFFFFF",
  "success": "#1E6B57",
  "warning": "#A15C0B",
  "warning-soft": "#F6E7D6",
  "warning-ink": "#7A3F0C",
  "danger": "#A1361F",
  "danger-soft": "#F6E1DB",
  "inverse": "#16181D",
  "inverse-text": "#FFFFFF",
  "radius": "16px",
  "radius-sm": "12px",
  "radius-tile": "18px",
  "space": "16px",
  "font": "\"IBM Plex Sans\", system-ui, -apple-system, \"Segoe UI\", sans-serif",
  "font-display": "\"Bricolage Grotesque\", \"IBM Plex Sans\", system-ui, sans-serif",
  "font-mono": "\"IBM Plex Mono\", ui-monospace, SFMono-Regular, Menlo, monospace",
  "shadow": "0 1px 2px rgb(22 24 29 / 5%)",
  "shadow-sheet": "0 -8px 30px rgb(22 24 29 / 12%)",
};
const DARK: Record<string, string> = { ...LIGHT, ...({
  "bg": "#111315",
  "surface": "#1A1D21",
  "surface-2": "#23272C",
  "border": "#2C3036",
  "border-strong": "#3B4048",
  "divider": "#24282D",
  "text": "#ECEDEF",
  "text-2": "#C9CCD2",
  "text-muted": "#9CA1AB",
  "accent": "#5BBE9E",
  "accent-hover": "#74CDAF",
  "accent-soft": "#17352C",
  "accent-contrast": "#0C1714",
  "success": "#5BBE9E",
  "warning": "#E3A55B",
  "warning-soft": "#3A2A17",
  "warning-ink": "#F2C58F",
  "danger": "#EA7B63",
  "danger-soft": "#3B1F19",
  "inverse": "#ECEDEF",
  "inverse-text": "#111315",
  "shadow": "0 1px 2px rgb(0 0 0 / 40%)",
  "shadow-sheet": "0 -8px 30px rgb(0 0 0 / 50%)",
}) };

interface ThemeState {
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => load<ThemePref>("omni.theme", "system"));
  const [systemDark, setSystemDark] = useState(() => matchMedia("(prefers-color-scheme: dark)").matches);

  useEffect(() => {
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const fn = () => setSystemDark(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const mode = pref === "system" ? (systemDark ? "dark" : "light") : pref;
  const theme = useMemo<Theme>(() => ({ mode, tokens: mode === "dark" ? DARK : LIGHT }), [mode]);

  useEffect(() => {
    const root = document.documentElement;
    for (const [k, v] of Object.entries(theme.tokens)) root.style.setProperty(`--omni-${k}`, v);
    root.dataset.omniTheme = theme.mode;
    root.style.colorScheme = theme.mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.tokens.surface);
  }, [theme]);

  const setPref = (p: ThemePref) => {
    setPrefState(p);
    save("omni.theme", p);
  };
  return <ThemeContext.Provider value={{ pref, setPref, theme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}
